import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import * as SERVICES from "src/services/jurisdictionServices";
import { getAccessToken } from "src/redux/login/loginReducer";
import { getUserOrganismsById } from "src/services/userServices";

export const useJurisdictions = () => {
  const [jurisdictionData, setJurisdictionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrganisms, setSelectedOrganisms] = useState([]);

  const [userOrganisms, setUserOrganisms] = useState([]);
  const [userOrganismsLoading, setUserOrganismsLoading] = useState(false);
  const [userOrganismsError, setUserOrganismsError] = useState(null);

  const accessToken = useSelector((state) => getAccessToken(state));

  // Traigo los organismos del usuario
  const fetchUserOrganisms = useCallback(async (userId) => {
    if (!userId || !accessToken) return;
    
    setUserOrganismsLoading(true);
    setUserOrganismsError(null);
    
    try {
      const response = await getUserOrganismsById(accessToken, userId);
      const organisms = response?.data || [];
      setUserOrganisms(organisms);
      
      const formattedOrganisms = organisms.map(organism => ({
        key: `organism-${organism?.id}`,
        title: `${organism?.id} - ${organism?.shortName || organism?.name}`,
        value: `organism-${organism?.id}`,
      }));
      
      return { raw: organisms, formatted: formattedOrganisms };
    } catch (error) {
      console.error("Error fetching user organisms:", error);
      setUserOrganismsError(error);
      setUserOrganisms([]);
      return { raw: [], formatted: [] };
    } finally {
      setUserOrganismsLoading(false);
    }
  }, [accessToken]);

  // Función para resetear organismos del usuario
  const resetUserOrganisms = useCallback(() => {
    setUserOrganisms([]);
    setUserOrganismsError(null);
  }, []);

  // Traigo el listado de Organismos por Jurisdiccion
  const fetchOrganismsByJurisdiction = useCallback(
    async (jurisdictionId) => {
      try {
        const response = await SERVICES.listOrganismsByJurisdictionId(
          accessToken,
          jurisdictionId
        );
        return response?.data || [];
      } catch (error) {
        console.error(
          `Error fetching organisms for jurisdiction ${jurisdictionId}:`,
          error
        );
        return [];
      }
    },
    [accessToken]
  );

  // Traigo el listado de jurisdicciones y asigno sus organismos dentro
  const fetchJurisdictions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await SERVICES.listJurisdiction(accessToken, 0, 100);
      const jurisdictions = response?.data?.records || [];

      const jurisdictionsWithOrganisms = await Promise.all(
        jurisdictions.map(async (jurisdiction) => {
          const organisms = await fetchOrganismsByJurisdiction(jurisdiction.id);

          const children = organisms.map((organism) => ({
            title: `${organism.codeBudget} - ${organism.shortName}`,
            value: `organism-${organism.id}`,
            key: `organism-${organism.id}`,
          }));

          return {
            title: `${jurisdiction.code} - ${jurisdiction.shortName}`,
            value: `jurisdiction-${jurisdiction.id}`,
            key: `jurisdiction-${jurisdiction.id}`,
            children: children.length > 0 ? children : undefined,
          };
        })
      );

      setJurisdictionData(jurisdictionsWithOrganisms);
    } catch (error) {
      console.error("Error fetching jurisdictions:", error);
      setJurisdictionData([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, fetchOrganismsByJurisdiction]);

  // En caso de seleccionar Jurisdiccion, devuelvo los IDs de organismos
  const processSelectedOrganisms = useCallback(
    (selectedValues) => {
      const organismIds = [];

      selectedValues.forEach((selectedValue) => {
        if (selectedValue.startsWith("organism-")) {
          const organismId = selectedValue.replace("organism-", "");
          organismIds.push(organismId);
        } else if (selectedValue.startsWith("jurisdiction-")) {
          const jurisdictionId = selectedValue.replace("jurisdiction-", "");
          const jurisdiction = jurisdictionData.find(
            (j) => j.key === `jurisdiction-${jurisdictionId}`
          );

          if (jurisdiction?.children) {
            jurisdiction.children.forEach((organism) => {
              const organismId = organism.value.replace("organism-", "");
              if (!organismIds.includes(organismId)) {
                organismIds.push(organismId);
              }
            });
          }
        }
      });

      return organismIds;
    },
    [jurisdictionData]
  );

  return {
    // Jurisdictions data
    jurisdictionData,
    isLoading,
    fetchJurisdictions,

    // User organisms data
    userOrganisms,
    userOrganismsLoading,
    userOrganismsError,
    fetchUserOrganisms,
    resetUserOrganisms,

    // Selection state
    selectedOrganisms,
    setSelectedOrganisms,

    // Utilities
    processSelectedOrganisms,
  };
};
